import React from 'react';
import { motion } from 'framer-motion';

/**
 * Kinetic heading reveal: splits text and staggers pieces up from behind an
 * overflow mask.
 *
 * `gradient` applies the site's gradient-text treatment — in that mode the
 * animation runs per *word*, because `background-clip: text` does not paint
 * through separately-composited (animated) descendant spans in Chrome.
 * `inView` triggers on scroll instead of on mount.
 */
const pieceVariant = {
  hidden: { y: '115%', opacity: 0, rotate: 8 },
  visible: {
    y: '0%',
    opacity: 1,
    rotate: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const maskStyle = {
  display: 'inline-block',
  overflow: 'hidden',
  verticalAlign: 'bottom',
  padding: '0.08em 0.02em 0.12em',
  margin: '-0.08em -0.02em -0.12em',
};

const SplitText = ({
  text,
  className,
  delay = 0,
  stagger = 0.028,
  inView = false,
  gradient = false,
}) => {
  const words = String(text).split(' ');
  const trigger = inView
    ? { initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-60px' } }
    : { initial: 'hidden', animate: 'visible' };

  return (
    <motion.span
      className={className}
      style={{ display: 'inline-block' }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: gradient ? stagger * 4 : stagger, delayChildren: delay } } }}
      aria-label={text}
      {...trigger}
    >
      {words.map((word, wi) => (
        <span key={`${word}-${wi}`} style={maskStyle} aria-hidden="true">
          {gradient ? (
            <motion.span
              variants={pieceVariant}
              className="gradient-text"
              style={{ display: 'inline-block' }}
            >
              {word}
            </motion.span>
          ) : (
            Array.from(word).map((ch, ci) => (
              <motion.span
                key={ci}
                variants={pieceVariant}
                style={{ display: 'inline-block' }}
              >
                {ch}
              </motion.span>
            ))
          )}
          {wi < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </motion.span>
  );
};

export default SplitText;
